import type { LandmarkName, VisionResult } from "@studystraight/shared-types";
import { useEffect, useRef } from "react";

/** Chỉ vẽ 7 điểm mà app dùng (§25) — vẽ cả 33 điểm chỉ làm rối và che mất điểm cần xem. */
const CONNECTIONS: readonly [LandmarkName, LandmarkName][] = [
  ["leftEar", "rightEar"],
  ["leftShoulder", "rightShoulder"],
  ["leftShoulder", "leftHip"],
  ["rightShoulder", "rightHip"],
  ["leftHip", "rightHip"],
  ["nose", "leftEar"],
  ["nose", "rightEar"],
];

/** Màu theo `visibility`: xanh = engine thực sự thấy, đỏ = đang suy đoán (DU #3). */
function visibilityColor(visibility: number | undefined, minVisibility: number): string {
  const v = visibility ?? 1;
  if (v >= minVisibility + 0.25) return "#34d399";
  if (v >= minVisibility) return "#fbbf24";
  return "#f87171";
}

export interface SkeletonCanvasProps {
  /** Đọc frame mới nhất mỗi lần vẽ. Không truyền qua prop để không re-render 12 lần/giây. */
  getVision: () => VisionResult | null;
  minVisibility: number;
  /** Khớp với `.preview--mirrored` của video: overlay phải lật cùng chiều với ảnh. */
  mirrored: boolean;
}

export function SkeletonCanvas({
  getVision,
  minVisibility,
  mirrored,
}: SkeletonCanvasProps): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let rafId = 0;

    const draw = (): void => {
      rafId = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;

      // Canvas backing store phải khớp kích thước hiển thị, nếu không skeleton lệch khỏi người
      // trên màn hình retina.
      const { clientWidth: w, clientHeight: h } = canvas;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      ctx.clearRect(0, 0, w, h);

      const vision = getVision();
      if (!vision) return;

      const px = (name: LandmarkName): [number, number] | null => {
        const lm = vision.landmarks[name];
        return lm ? [lm.x * w, lm.y * h] : null;
      };

      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(79, 140, 255, 0.85)";
      for (const [from, to] of CONNECTIONS) {
        const a = px(from);
        const b = px(to);
        if (!a || !b) continue;
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.stroke();
      }

      for (const lm of Object.values(vision.landmarks)) {
        if (!lm) continue;
        ctx.fillStyle = visibilityColor(lm.visibility, minVisibility);
        ctx.beginPath();
        ctx.arc(lm.x * w, lm.y * h, 5, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [getVision, minVisibility]);

  return (
    <canvas
      ref={canvasRef}
      className={`skeleton${mirrored ? " preview--mirrored" : ""}`}
      aria-hidden="true"
    />
  );
}
